import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Alert,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { DriverData } from './types';
import { ImageGalleryModal } from '../../components/ImageGalleryModal';

interface Certification {
  id: number;
  certificationTypeId: number;
  otherName: string;
  description: string;
  completedDate: string;
  expiryDate: string;
  fileId?: number;
  file?: any;
}

interface CertificationsTabProps {
  driverData: DriverData | null;
  certificationTypes?: any[];
  onRefresh?: () => void;
}

export const CertificationsTab: React.FC<CertificationsTabProps> = ({ driverData, certificationTypes = [], onRefresh }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCertification, setEditingCertification] = useState<Certification | null>(null);
  const [formData, setFormData] = useState({
    certificationTypeId: '',
    certificationTitle: '',
    certificationSummary: '',
    completedDate: '',
    expiryDate: '',
  });
  const [uploadedFileId, setUploadedFileId] = useState<number | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [viewingImage, setViewingImage] = useState<Certification | null>(null);

  const certifications = (driverData?.certifications || []) as Certification[];

  // Image viewer state
  const imageGalleryRef = useRef<any>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const openImageViewer = (cert: Certification) => {
    if (!cert.fileId || !cert.file?.mimeType?.startsWith('image/')) return;
    setGalleryImages([
      {
        id: cert.fileId,
        thumbnail: cert.file.thumbnail,
        mimeType: cert.file.mimeType,
      },
    ]);
    setGalleryIndex(0);
    setGalleryOpen(true);
    imageGalleryRef.current?.handleOpenModal(0);
  };

  // Helper function to format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateCertification = (): boolean => {
    const errors: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Completion date is mandatory
    if (!formData.completedDate) {
      errors.push('Certification completed date is required and must occur in the past');
    } else {
      // Check if completion date is in the past
      const completedDate = new Date(formData.completedDate);
      if (completedDate > today) {
        errors.push('Certification completed date is required and must occur in the past');
      }
    }

    // If expiry date is set, it must be after completion date
    if (formData.expiryDate && formData.completedDate) {
      const completedDate = new Date(formData.completedDate);
      const expiryDate = new Date(formData.expiryDate);
      if (expiryDate <= completedDate) {
        errors.push('Certification expiry date must be greater than completed date');
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleAddCertification = () => {
    setEditingCertification(null);
    setFormData({
      certificationTypeId: '',
      certificationTitle: '',
      certificationSummary: '',
      completedDate: '',
      expiryDate: '',
    });
    setUploadedFileId(null);
    setFileName('');
    setDialogOpen(true);
  };

  const handleEditCertification = (certification: Certification) => {
    setEditingCertification(certification);
    setFormData({
      certificationTypeId: certification.certificationTypeId.toString(),
      certificationTitle: certification.otherName,
      certificationSummary: certification.description,
      completedDate: formatDateForInput(certification.completedDate),
      expiryDate: formatDateForInput(certification.expiryDate),
    });
    setUploadedFileId(certification.fileId || null);
    setFileName(certification.file?.name || '');
    setDialogOpen(true);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingFile(true);

      // Step 1: Upload file to /files endpoint
      const result = await dataProvider.create('files', {
        data: {
          file,
          name: file.name,
          folder: 'driver-certifications',
          description: `Certification file for driver ${driverData?.id}`,
        },
      });

      // Step 2: Store the fileId (not the raw file)
      const uploadedFileIdFromResponse = result.data?.id;
      setUploadedFileId(uploadedFileIdFromResponse);
      setFileName(file.name);
      notify('File uploaded successfully', { type: 'success' });
    } catch (error: any) {
      notify('Error uploading file: ' + (error.message || 'Unknown error'), { type: 'error' });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleSaveCertification = async () => {
    if (!formData.certificationTypeId || !formData.certificationTitle) {
      notify('Please fill in all required fields', { type: 'warning' });
      return;
    }

    if (!validateCertification()) {
      return;
    }

    try {
      if (editingCertification) {
        const updateData: any = {
          certificationTypeId: parseInt(formData.certificationTypeId as string),
          otherName: formData.certificationTitle,
          description: formData.certificationSummary,
          completedDate: formData.completedDate,
          expiryDate: formData.expiryDate || null,
        };

        // Use uploadedFileId if a new file was uploaded, otherwise keep existing fileId
        if (uploadedFileId) {
          updateData.fileId = uploadedFileId;
        }

        await dataProvider.update('drivers/certifications', {
          id: editingCertification.id,
          data: updateData,
          previousData: editingCertification,
        });
        notify('Certification updated successfully', { type: 'success' });
      } else {
        const createData: any = {
          driverId: driverData?.id,
          certificationTypeId: parseInt(formData.certificationTypeId as string),
          otherName: formData.certificationTitle,
          description: formData.certificationSummary,
          completedDate: formData.completedDate,
          expiryDate: formData.expiryDate || null,
        };

        // Use uploadedFileId if a file was uploaded
        if (uploadedFileId) {
          createData.fileId = uploadedFileId;
        }

        await dataProvider.create('drivers/certifications', {
          data: createData,
        });
        notify('Certification added successfully', { type: 'success' });
      }
      setDialogOpen(false);
      onRefresh?.();
    } catch (error: any) {
      notify('Error saving certification: ' + (error.message || 'Unknown error'), { type: 'error' });
    }
  };

  const handleDeleteCertification = async (certification: Certification) => {
    if (window.confirm('Are you sure you want to delete this certification?')) {
      try {
        await dataProvider.delete('drivers/certifications', { id: certification.id });
        notify('Certification deleted successfully', { type: 'success' });
        onRefresh?.();
      } catch (error: any) {
        notify('Error deleting certification: ' + (error.message || 'Unknown error'), { type: 'error' });
      }
    }
  };

  const handleDialogClose = () => {
    setFormData({
      certificationTypeId: '',
      certificationTitle: '',
      certificationSummary: '',
      completedDate: '',
      expiryDate: '',
    });
    setUploadedFileId(null);
    setFileName('');
    setValidationErrors([]);
    setDialogOpen(false);
  };

  const getCertificationType = (typeId: number) => {
    return certificationTypes.find(ct => ct.id === typeId)?.name || `Type ${typeId}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Certifications</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={handleAddCertification}
        >
          Add Certification
        </Button>
      </Box>

      {/* Table */}
      {certifications.length > 0 ? (
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {certifications.map(certification => (
              <TableRow key={certification.id}>
                <TableCell>{certification.otherName}</TableCell>
                <TableCell>{getCertificationType(certification.certificationTypeId)}</TableCell>
                <TableCell>{new Date(certification.completedDate).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(certification.expiryDate).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEditCertification(certification)}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No certifications added yet
        </Typography>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{editingCertification ? 'Edit Certification' : 'Add Certification'}</Typography>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, overflow: 'visible', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1' }, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '6px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#555' } }}>
          <Stack spacing={2}>
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {validationErrors.map((error, idx) => (
                  <div key={idx}>{error}</div>
                ))}
              </Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Certification Type</InputLabel>
              <Select
                value={formData.certificationTypeId}
                onChange={(e) => setFormData({ ...formData, certificationTypeId: e.target.value })}
                label="Certification Type"
              >
                <MenuItem value="">Select certification type</MenuItem>
                {certificationTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <MuiTextField
              size="small"
              label="Title"
              fullWidth
              value={formData.certificationTitle}
              onChange={(e) => setFormData({ ...formData, certificationTitle: e.target.value })}
              placeholder="e.g., CPR Certification"
            />

            <MuiTextField
              size="small"
              label="Summary"
              fullWidth
              multiline
              rows={3}
              value={formData.certificationSummary}
              onChange={(e) => setFormData({ ...formData, certificationSummary: e.target.value })}
              placeholder="Describe the certification"
            />

            <MuiTextField
              size="small"
              label="Completion Date"
              type="date"
              fullWidth
              required
              value={formData.completedDate}
              onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <MuiTextField
              size="small"
              label="Expiry Date"
              type="date"
              fullWidth
              value={formData.expiryDate}
              onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Attach Certificate/Document
              </Typography>

              {/* Show uploaded file with remove button */}
              {uploadedFileId && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>✓ {fileName}</Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setUploadedFileId(null);
                      setFileName('');
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}

              {/* File upload area - only show if no file uploaded */}
              {!uploadedFileId && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: isUploadingFile ? 'wait' : 'pointer',
                    backgroundColor: isUploadingFile ? '#f0f0f0' : '#fafafa',
                    '&:hover': { backgroundColor: isUploadingFile ? '#f0f0f0' : '#f5f5f5' },
                    border: '2px dashed #ccc',
                    opacity: isUploadingFile ? 0.6 : 1,
                    pointerEvents: isUploadingFile ? 'none' : 'auto',
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    disabled={isUploadingFile}
                    style={{ display: 'none' }}
                    id="certification-file-input"
                  />
                  <label htmlFor="certification-file-input" style={{ cursor: isUploadingFile ? 'wait' : 'pointer', display: 'block' }}>
                    {isUploadingFile ? (
                      <Typography variant="body2" color="text.secondary">
                        Uploading file...
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click to select a file (PDF, DOC, DOCX, JPG, PNG)
                      </Typography>
                    )}
                  </label>
                </Paper>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveCertification} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Viewer */}
      <ImageGalleryModal
        ref={imageGalleryRef}
        images={galleryImages}
        imageDefaultIndex={galleryIndex}
        openModal={galleryOpen}
        setOpenModal={setGalleryOpen}
      >
        <></>
      </ImageGalleryModal>
    </Box>
  );
};
